import { supabase } from 'config/supabase';

// Simple Supabase DB helpers for MVP
// This file will contain basic read/write/update/upload helpers as needed
// We'll add to this file as and when we create features that need database operations

// Import the actual OnboardingData interface from the onboarding types
import { OnboardingData } from 'pages/Onboarding/types';
import { Message, ChatRequest, ChatResponse } from 'types';
import { transformMessages } from './messageUtils';
import { ContentLibrary as ContentLibraryType } from 'types';
import { getFileType } from './fileTypeUtils';

// Extended interface for database storage with completion metadata
export interface OnboardingDataWithCompletion extends OnboardingData {
  isCompleted: boolean;
  completedAt?: string;
}

// Fetch conversation history for the current user
export const fetchHistory = async () => {
  const { data, error } = await supabase
    .from('conversations')
    .select('id, title, updated_at')
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching history:', error);
    return { data: [], error };
  }

  // Transform data to match expected format
  const historyItems =
    data?.map((conv) => ({
      id: conv.id,
      title: conv.title || 'Untitled Conversation',
      date: new Date(conv.updated_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    })) || [];

  return { data: historyItems, error: null };
};

// Process social examples from onboarding data and save to social_examples table
const processSocialExamplesFromOnboarding = async (
  onboardingData: OnboardingData,
  userId: string,
) => {
  try {
    const socialExamples = onboardingData.step7_brandAssets?.socialExamples;

    if (!socialExamples) {
      console.log('No social examples found in onboarding data');
      return;
    }

    const twitterExamples = socialExamples.twitterExamples || [];
    const linkedinExamples = socialExamples.linkedinExamples || [];
    const blogUrls = socialExamples.blogUrls || [];

    console.log('Processing social examples:', {
      twitterExamplesCount: twitterExamples.length,
      linkedinExamplesCount: linkedinExamples.length,
      blogUrlsCount: blogUrls.length,
    });

    // Delete existing manual input examples for all platforms
    const { error: deleteError } = await supabase
      .from('social_examples')
      .delete()
      .eq('user_id', userId)
      .eq('source', 'manual_input');

    if (deleteError) {
      console.error('Error deleting existing manual examples:', deleteError);
      // Don't throw error, just log it and continue
    }

    // Process Twitter examples (multiple)
    if (twitterExamples.length > 0) {
      for (const example of twitterExamples) {
        if (example && example.trim()) {
          const twitterExample = {
            user_id: userId,
            platform: 'twitter',
            content: example.trim(),
            source: 'manual_input' as const,
            url: null,
            engagement_metrics: {},
            is_selected: true,
          };

          const { error: twitterError } = await supabase
            .from('social_examples')
            .insert(twitterExample);

          if (twitterError) {
            console.error('Error saving Twitter example:', twitterError);
          } else {
            console.log('Successfully saved Twitter example');
          }
        }
      }
    }

    // Process LinkedIn examples (multiple)
    if (linkedinExamples.length > 0) {
      for (const example of linkedinExamples) {
        if (example && example.trim()) {
          const linkedinExample = {
            user_id: userId,
            platform: 'linkedin',
            content: example.trim(),
            source: 'manual_input' as const,
            url: null,
            engagement_metrics: {},
            is_selected: true,
          };

          const { error: linkedinError } = await supabase
            .from('social_examples')
            .insert(linkedinExample);

          if (linkedinError) {
            console.error('Error saving LinkedIn example:', linkedinError);
          } else {
            console.log('Successfully saved LinkedIn example');
          }
        }
      }
    }

    // Process Blog URLs (multiple) - content will be scraped by process-brand-materials
    if (blogUrls.length > 0) {
      for (const blogUrl of blogUrls) {
        if (blogUrl && blogUrl.trim()) {
          const blogExample = {
            user_id: userId,
            platform: 'blog',
            content: null, // Will be populated by Firecrawl scraping
            source: 'manual_input' as const,
            url: blogUrl.trim(),
            engagement_metrics: {},
            is_selected: true,
          };

          const { error: blogError } = await supabase.from('social_examples').insert(blogExample);

          if (blogError) {
            console.error('Error saving blog URL:', blogError);
          } else {
            console.log('Successfully saved blog URL for scraping:', blogUrl);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error processing social examples from onboarding:', error);
    // Don't throw error to avoid breaking the entire onboarding save process
  }
};

// Save onboarding data to brand_materials table and process for RAG
export const saveOnboardingData = async (onboardingData: OnboardingDataWithCompletion) => {
  try {
    // Get current user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Get session for auth token
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No session found');
    }

    // Step 1: Save the onboarding form data
    console.log('Processing onboarding form data for user:', user.id);

    // Check if user already has onboarding data (get the most recent one)
    const { data: existingMaterials, error: checkError } = await supabase
      .from('brand_materials')
      .select('id, updated_at')
      .eq('user_id', user.id)
      .eq('type', 'form_data')
      .eq('file_name', 'onboarding_responses')
      .order('updated_at', { ascending: false })
      .limit(1);

    const existingMaterial = existingMaterials?.[0]; // Get the first (most recent) record

    if (checkError) {
      console.error('Error checking existing onboarding data:', checkError);
      throw checkError;
    }

    let formMaterialId: string;

    if (existingMaterial) {
      // RE-SUBMIT: Smart cleanup - only delete what's necessary
      console.log('Re-submit detected: Smart cleanup for user:', user.id);

      // ==========================================
      // STEP 1: Handle deleted files (explicitly removed by user)
      // ==========================================
      const deletedMaterialIds = onboardingData.step7_brandAssets.deletedMaterialIds || [];

      if (deletedMaterialIds.length > 0) {
        console.log(`Deleting ${deletedMaterialIds.length} explicitly removed files`);

        // Get file paths for storage cleanup
        const { data: deletedFiles } = await supabase
          .from('brand_materials')
          .select('id, pdf_url')
          .in('id', deletedMaterialIds);

        if (deletedFiles && deletedFiles.length > 0) {
          const deletedFilePaths = deletedFiles.filter((f) => f.pdf_url).map((f) => f.pdf_url);

          // Delete chunks (CASCADE will handle this, but being explicit)
          await supabase
            .from('brand_content_chunks')
            .delete()
            .in('material_id', deletedMaterialIds);

          // Delete from Storage
          if (deletedFilePaths.length > 0) {
            await supabase.storage.from('brand-materials').remove(deletedFilePaths);
            console.log(`Deleted ${deletedFilePaths.length} files from storage`);
          }

          // Delete brand_materials entries
          await supabase.from('brand_materials').delete().in('id', deletedMaterialIds);

          console.log('Deleted explicitly removed files');
        }
      }

      // ==========================================
      // STEP 2: Delete old embeddings for form_data (always refresh)
      // ==========================================
      await supabase.from('brand_content_chunks').delete().eq('material_id', existingMaterial.id);

      console.log('Deleted old form_data chunks');

      // ==========================================
      // STEP 3: Update form_data entry
      // ==========================================
      const { error: updateError } = await supabase
        .from('brand_materials')
        .update({
          form_data: onboardingData,
          processing_status: 'pending',
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingMaterial.id);

      if (updateError) {
        console.error('Error updating onboarding data:', updateError);
        throw updateError;
      }

      console.log('Updated form_data entry');
      formMaterialId = existingMaterial.id;
    } else {
      // INSERT new onboarding data (first time)
      console.log('Inserting new onboarding form data for user:', user.id);

      const { data: brandMaterial, error: insertError } = await supabase
        .from('brand_materials')
        .insert({
          user_id: user.id,
          type: 'form_data',
          file_name: 'onboarding_responses',
          form_data: onboardingData,
          processing_status: 'pending',
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error inserting onboarding data:', insertError);
        throw insertError;
      }

      formMaterialId = brandMaterial.id;
    }

    // Step 2: Process brand material files from step7_brandAssets (PDF, TXT, DOC, DOCX)
    const brandFiles = onboardingData.step7_brandAssets.files || [];
    console.log(`Processing ${brandFiles.length} brand material files from onboarding`);

    for (const brandFile of brandFiles) {
      try {
        // Extract the actual File object from the UploadedFile structure
        const actualFile = (brandFile as any).file || brandFile;
        const fileName = (brandFile as any).name || actualFile.name;
        const storageUrl = (brandFile as any).storageUrl; // URL from autofill
        const materialId = (brandFile as any).materialId; // ID from existing material

        // ==========================================
        // HANDLE THREE CASES:
        // 1. New file with File blob → Upload to storage
        // 2. Autofilled file with storageUrl but no blob → Create DB entry
        // 3. Existing file with materialId → Skip (already in DB)
        // ==========================================

        // Case 3: Skip existing files (already have materialId)
        if (materialId) {
          console.log(`Skipping file ${fileName} - already in DB (materialId: ${materialId})`);
          continue;
        }

        // Case 2: Autofilled file (has storageUrl but no File blob)
        if (storageUrl && !actualFile.size && !actualFile.type) {
          console.log(`Processing autofilled file: ${fileName} from ${storageUrl}`);

          // Determine file type from extension
          const fileExtension = fileName.split('.').pop()?.toLowerCase();
          let fileType = 'pdf_upload'; // Default
          if (fileExtension === 'txt') {
            fileType = 'txt_upload';
          } else if (fileExtension === 'doc') {
            fileType = 'doc_upload';
          } else if (fileExtension === 'docx') {
            fileType = 'docx_upload';
          }

          // Create brand_materials entry for autofilled file
          const { error: insertError } = await supabase.from('brand_materials').insert({
            user_id: user.id,
            type: fileType,
            file_name: fileName,
            pdf_url: storageUrl, // Already in storage from autofill
            processing_status: 'pending',
          });

          if (insertError) {
            console.error(
              `Error creating brand materials entry for autofilled file ${fileName}:`,
              insertError,
            );
          } else {
            console.log(`Successfully created DB entry for autofilled file: ${fileName}`);
          }
          continue;
        }

        // Case 1: New file with File blob → Upload to storage
        const isActualFile =
          actualFile instanceof File ||
          (actualFile.size && actualFile.type && actualFile.lastModified);

        if (!isActualFile) {
          console.log(`Skipping file ${fileName} - no File blob and no storageUrl`);
          continue;
        }

        // Determine file type dynamically based on MIME type and extension
        const fileType = getFileType(actualFile);

        console.log(
          `Uploading NEW file: ${fileName} (type: ${fileType}), size: ${actualFile.size}`,
        );

        // Upload file to Supabase Storage
        const filePath = `${user.id}/brand-materials/${Date.now()}_${fileName}`;
        const { data, error: uploadError } = await supabase.storage
          .from('brand-materials')
          .upload(filePath, actualFile, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.error(`Error uploading file ${fileName}:`, uploadError);
          continue; // Skip this file but continue with others
        }

        console.log(`Successfully uploaded file to storage: ${data.path}`);

        // Create brand_materials entry with dynamic file type
        const { error: insertError } = await supabase.from('brand_materials').insert({
          user_id: user.id,
          type: fileType, // Dynamic type: pdf_upload, txt_upload, doc_upload, or docx_upload
          file_name: fileName,
          pdf_url: data.path, // Store just the path (column name is historical, used for all file types)
          processing_status: 'pending',
        });

        if (insertError) {
          console.error(`Error creating brand materials entry for ${fileName}:`, insertError);
          continue; // Skip this file but continue with others
        }

        console.log(`Successfully processed file: ${fileName} (${fileType})`);
      } catch (error) {
        console.error(`Error processing file ${(brandFile as any).name || 'unknown'}:`, error);
        continue; // Skip this file but continue with others
      }
    }

    // Step 3: Process social examples from onboarding data
    console.log('Processing social examples from onboarding data');
    await processSocialExamplesFromOnboarding(onboardingData, user.id);

    // Step 4: Process all materials (form data + brand files) for RAG
    console.log('Triggering RAG processing for all materials');
    await processAllBrandMaterials();

    return { formMaterialId, fileCount: brandFiles.length };
  } catch (error) {
    console.error('Failed to save onboarding data:', error);
    throw error;
  }
};

// Fetch onboarding data for the current user
export const fetchOnboardingData = async () => {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('brand_materials')
      .select('form_data')
      .eq('user_id', user.id)
      .eq('type', 'form_data')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') {
      // PGRST116 is "not found"
      throw error;
    }

    let formData = data?.form_data || null;

    // If we have form_data, also fetch existing file materials
    // and populate step7_brandAssets.files with them
    if (formData && user) {
      // Get list of files that should be excluded (marked for deletion)
      const deletedMaterialIds = formData.step7_brandAssets?.deletedMaterialIds || [];

      const { data: existingFiles } = await supabase
        .from('brand_materials')
        .select('id, file_name, pdf_url')
        .eq('user_id', user.id)
        .in('type', ['pdf_upload', 'txt_upload', 'docx_upload', 'doc_upload']);

      if (existingFiles && existingFiles.length > 0) {
        // Filter out files that are marked for deletion
        const activeFiles = existingFiles.filter((file) => !deletedMaterialIds.includes(file.id));

        // Get current files from form_data (may have sizes from autofill)
        const currentFiles = formData.step7_brandAssets?.files || [];

        // Add existing files from database ONLY if they're not already in currentFiles
        const existingFileObjects = activeFiles
          .filter((dbFile) => {
            // Check if this file is already in currentFiles
            return !currentFiles.some((formFile: any) => formFile.name === dbFile.file_name);
          })
          .map((file) => ({
            id: file.id, // Use material_id as the id
            file: {}, // Empty file blob (already uploaded)
            name: file.file_name,
            size: 0, // Size unknown (already uploaded)
            materialId: file.id, // Track the database ID
            storageUrl: file.pdf_url, // Track the storage path
          }));

        // Enrich currentFiles with materialId if they match database files
        const enrichedCurrentFiles = currentFiles.map((formFile: any) => {
          const matchingDbFile = activeFiles.find((dbFile) => dbFile.file_name === formFile.name);
          if (matchingDbFile) {
            return {
              ...formFile,
              materialId: matchingDbFile.id, // Add materialId from database
              storageUrl: matchingDbFile.pdf_url, // Add storageUrl from database
            };
          }
          return formFile;
        });

        formData = {
          ...formData,
          step7_brandAssets: {
            ...formData.step7_brandAssets,
            files: [...enrichedCurrentFiles, ...existingFileObjects],
            deletedMaterialIds: [], // Reset after filtering
          },
        };
      }
    }

    return formData;
  } catch (error) {
    console.error('Error fetching onboarding data:', error);
    return null;
  }
};

// Save account settings (company name, etc.)
export const saveAccountSettings = async (data: { companyName: string; email: string }) => {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Update user metadata
    const { error: updateError } = await supabase.auth.updateUser({
      data: {
        company_name: data.companyName,
      },
    });

    if (updateError) {
      throw updateError;
    }

    return { success: true };
  } catch (error) {
    console.error('Error saving account settings:', error);
    throw error;
  }
};

// Load conversation with messages
export const loadConversation = async (conversationId: string): Promise<Message[]> => {
  try {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('User not authenticated');
    }

    // Fetch conversation messages
    const { data: messages, error } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (error) {
      throw error;
    }

    // Transform messages to match Message interface
    return transformMessages(messages);
  } catch (error) {
    console.error('Error loading conversation:', error);
    throw error;
  }
};

// Send message to chat orchestrator
export const sendMessage = async (request: ChatRequest): Promise<ChatResponse> => {
  try {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chat-orchestrator`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Transform assistant messages to match Message interface
    return {
      conversation_id: data.conversation_id,
      messages: transformMessages(data.messages),
      metadata: data.metadata || {
        tokens_used: 0,
        duration_ms: 0,
        agents_invoked: [],
      },
    };
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Avatar Upload and Management
export const uploadAvatar = async (file: File): Promise<string> => {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Generate file path: user_id/avatar.ext
    const fileExt = file.name.split('.').pop();
    const filePath = `${user.id}/avatar.${fileExt}`;

    // Upload to storage (will overwrite if exists)
    const { error: uploadError } = await supabase.storage
      .from('user-avatars')
      .upload(filePath, file, {
        upsert: true, // Overwrite existing avatar
        contentType: file.type,
      });

    if (uploadError) {
      throw uploadError;
    }

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from('user-avatars').getPublicUrl(filePath);

    // Update user metadata with avatar URL
    const { error: updateError } = await supabase.auth.updateUser({
      data: {
        avatar_url: publicUrl,
      },
    });

    if (updateError) {
      throw updateError;
    }

    return publicUrl;
  } catch (error) {
    console.error('Error uploading avatar:', error);
    throw error;
  }
};

// Delete user avatar
export const deleteAvatar = async (): Promise<void> => {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // List all files in user's folder
    const { data: files, error: listError } = await supabase.storage
      .from('user-avatars')
      .list(user.id);

    if (listError) {
      throw listError;
    }

    // Delete all avatar files
    if (files && files.length > 0) {
      const filePaths = files.map((file) => `${user.id}/${file.name}`);
      const { error: deleteError } = await supabase.storage.from('user-avatars').remove(filePaths);

      if (deleteError) {
        throw deleteError;
      }
    }

    // Clear avatar URL from metadata
    const { error: updateError } = await supabase.auth.updateUser({
      data: {
        avatar_url: null,
      },
    });

    if (updateError) {
      throw updateError;
    }
  } catch (error) {
    console.error('Error deleting avatar:', error);
    throw error;
  }
};

// Get user metadata (first name, last name, company name, avatar URL)
export const getUserMetadata = async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.user_metadata || {};
};

// Batch Processing for RAG Embeddings
// Mark all user's brand materials as pending for processing
export const markAllMaterialsPending = async (): Promise<void> => {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Update all materials with 'completed' status to 'pending'
    const { error: updateError } = await supabase
      .from('brand_materials')
      .update({ processing_status: 'pending' })
      .eq('user_id', user.id)
      .eq('processing_status', 'completed');

    if (updateError) {
      throw updateError;
    }
  } catch (error) {
    console.error('Error marking materials as pending:', error);
    throw error;
  }
};

// Process all pending brand materials (both PDFs and form data) AND blog/website examples
export const processAllBrandMaterials = async (): Promise<void> => {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Get session for auth token
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No session found');
    }

    // ==========================================
    // PROCESS BRAND MATERIALS
    // ==========================================
    const { data: materials, error: fetchError } = await supabase
      .from('brand_materials')
      .select('id, type')
      .eq('user_id', user.id)
      .eq('processing_status', 'pending');

    if (fetchError) {
      throw fetchError;
    }

    // Process each material sequentially
    if (materials && materials.length > 0) {
      for (const material of materials) {
        console.log(`Processing material ${material.id} with type: ${material.type}`);

        const response = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/process-brand-materials`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              material_id: material.id,
              type: material.type,
            }),
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Processing failed for material ${material.id}:`, errorText);

          // Try to parse error response to get actual error message
          let errorMessage = `Processing failed for material ${material.id}`;
          try {
            const errorJson = JSON.parse(errorText);
            if (errorJson.error) {
              errorMessage = `${errorMessage}: ${errorJson.error}`;
            }
          } catch {
            // If not JSON, use the raw error text
            if (errorText) {
              errorMessage = `${errorMessage}: ${errorText}`;
            }
          }

          throw new Error(errorMessage);
        }
      }
      console.log(`Successfully processed ${materials.length} brand materials`);
    } else {
      console.log('No pending brand materials to process');
    }

    // ==========================================
    // PROCESS BLOG/WEBSITE EXAMPLES (need scraping)
    // ==========================================
    const { data: blogExamples, error: blogFetchError } = await supabase
      .from('social_examples')
      .select('id, platform, url')
      .eq('user_id', user.id)
      .in('platform', ['blog', 'website'])
      .or('content.is.null,content.eq.');

    if (blogFetchError) {
      console.error('Error fetching blog examples:', blogFetchError);
      // Don't throw - continue with what we have
    }

    if (blogExamples && blogExamples.length > 0) {
      console.log(`Processing ${blogExamples.length} blog/website examples`);

      for (const example of blogExamples) {
        if (!example.url) {
          console.log(`Skipping example ${example.id} - no URL`);
          continue;
        }

        console.log(`Scraping ${example.platform} example ${example.id}: ${example.url}`);

        try {
          const response = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/process-brand-materials`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                example_id: example.id,
                platform: example.platform,
              }),
            },
          );

          if (!response.ok) {
            const errorText = await response.text();
            console.error(`Scraping failed for example ${example.id}:`, errorText);
            // Continue with other examples even if one fails
          } else {
            console.log(`Successfully scraped example ${example.id}`);
          }
        } catch (scrapeError) {
          console.error(`Error scraping example ${example.id}:`, scrapeError);
          // Continue with other examples even if one fails
        }
      }

      console.log(`Finished processing ${blogExamples.length} blog/website examples`);
    } else {
      console.log('No blog/website examples to scrape');
    }
  } catch (error) {
    console.error('Error processing brand materials:', error);
    throw error;
  }
};

// Content Library Functions

// Marketing strategist sub-type filters (page_type values)
const MARKETING_LANDING_PAGE_TYPES = [
  'landing_page',
  'product',
  'persona',
  'competitor',
  'integration',
];
const MARKETING_BLOG_TYPES = ['blog_post'];
const MARKETING_STRATEGY_TYPES = ['positioning_document', 'website_structure'];

export const fetchContentLibrary = async (
  selectedType: string = 'all',
): Promise<ContentLibraryType[]> => {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    let query = supabase
      .from('content_library')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_hidden', false)
      .order('created_at', { ascending: false });

    // Handle marketing strategist sub-type filters
    if (selectedType === 'marketing_landing_page') {
      query = query.eq('content_type', 'marketing_strategist_agent');
      // Filter by page_type using OR conditions
      const pageTypeFilters = MARKETING_LANDING_PAGE_TYPES.map(
        (pt) => `content->agent_output->>page_type.eq.${pt}`,
      ).join(',');
      query = query.or(pageTypeFilters);
    } else if (selectedType === 'marketing_blog') {
      query = query.eq('content_type', 'marketing_strategist_agent');
      const pageTypeFilters = MARKETING_BLOG_TYPES.map(
        (pt) => `content->agent_output->>page_type.eq.${pt}`,
      ).join(',');
      query = query.or(pageTypeFilters);
    } else if (selectedType === 'marketing_strategy') {
      query = query.eq('content_type', 'marketing_strategist_agent');
      const pageTypeFilters = MARKETING_STRATEGY_TYPES.map(
        (pt) => `content->agent_output->>page_type.eq.${pt}`,
      ).join(',');
      query = query.or(pageTypeFilters);
    } else if (selectedType !== 'all') {
      query = query.eq('content_type', selectedType);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching content library:', error);
    throw error;
  }
};

// Update conversation title
export const updateConversationTitle = async (conversationId: string, newTitle: string) => {
  try {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('conversations')
      .update({ title: newTitle })
      .eq('id', conversationId)
      .eq('user_id', session.user.id);

    if (error) {
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating conversation title:', error);
    throw error;
  }
};

// Delete conversation and all its messages
export const deleteConversation = async (conversationId: string) => {
  try {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', conversationId)
      .eq('user_id', session.user.id);

    if (error) {
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
};

// Update user metadata (first name, last name, company name)
export const updateUserData = async (userData: {
  firstName?: string;
  lastName?: string;
  companyName?: string;
}) => {
  try {
    const { error: updateError } = await supabase.auth.updateUser({
      data: {
        first_name: userData.firstName,
        last_name: userData.lastName,
        company_name: userData.companyName,
      },
    });

    if (updateError) {
      throw updateError;
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating user data:', error);
    throw error;
  }
};

// ===================================
// Autofill Onboarding Helpers
// ===================================

/**
 * Upload files to Supabase Storage and return their URLs
 * Used for autofill feature to upload documents before processing
 */
export const uploadFilesToStorage = async (files: File[]): Promise<string[]> => {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const uploadedUrls: string[] = [];

    for (const file of files) {
      // Create unique file path
      const filePath = `${user.id}/brand-materials/${Date.now()}_${file.name}`;

      // Upload to storage
      const { data, error: uploadError } = await supabase.storage
        .from('brand-materials')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error(`Error uploading file ${file.name}:`, uploadError);
        throw uploadError;
      }

      uploadedUrls.push(data.path);
      console.log(`Uploaded ${file.name} to ${data.path}`);
    }

    return uploadedUrls;
  } catch (error) {
    console.error('Error uploading files to storage:', error);
    throw error;
  }
};

/**
 * Call autofill-onboarding edge function to extract OnboardingData from files
 * Returns extracted data in OnboardingData format
 */
export const autofillOnboarding = async (
  fileUrls: string[],
): Promise<{
  success: boolean;
  onboarding_data: OnboardingData;
  uploaded_files?: Array<{ url: string; name: string; size?: number }>;
}> => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No session found');
    }

    // Call edge function
    const { data, error } = await supabase.functions.invoke('autofill-onboarding', {
      body: { file_urls: fileUrls },
    });

    if (error) {
      console.error('Error calling autofill-onboarding function:', error);
      throw error;
    }

    if (!data.success) {
      throw new Error('Autofill failed');
    }

    return data;
  } catch (error) {
    console.error('Error in autofillOnboarding:', error);
    throw error;
  }
};

export default supabase;
